import React, { useEffect, useState } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import axios from 'axios';
import BaseURL from '../../../assets/contants/BaseURL';
import useLiveValue from '../useLiveValue';
import useMeterField from '../useMeterField';

function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startAngle, endAngle) {
    const p1 = polarToCartesian(cx, cy, r, startAngle);
    const p2 = polarToCartesian(cx, cy, r, endAngle);
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${p2.x} ${p2.y}`;
}

const ProgressWidget = ({ config }) => {
    const { dataSource = {}, settings = {}, variant = 'ring', title } = config;
    const max = settings.max ?? 100;
    const [fetchedValue, setFetchedValue] = useState(null);
    const isPreview = typeof config.id === 'string' && config.id.startsWith('preview-');
    const report = useMeterField(title, { meterId: settings.meterId, skip: Boolean(dataSource.endpoint) || isPreview });
    const liveValue = useLiveValue(settings.value ?? 0, {
        id: config.id, endpoint: dataSource.endpoint || (report.field ? 'report' : ''), min: 0, max,
    });

    useEffect(() => {
        if (!dataSource.endpoint) return;
        let cancelled = false;
        const token = sessionStorage.getItem('token');

        axios.get(`${BaseURL}${dataSource.endpoint}`, {
            headers: { Authorization: `Token ${token}` },
        })
            .then((res) => {
                if (cancelled) return;
                const data = res.data;
                setFetchedValue(Number(dataSource.field ? data?.[dataSource.field] : data) || 0);
            })
            .catch((err) => console.error('ProgressWidget: failed to fetch', err));

        return () => { cancelled = true; };
    }, [dataSource.endpoint, dataSource.field]);

    const value = dataSource.endpoint
        ? (fetchedValue ?? settings.value ?? 0)
        : (report.field ? (report.latest ?? 0) : liveValue);
    const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    const accent = settings.color || '#4318FF';
    const textColor = settings.textColor || accent;
    const tooltip = `${value} / ${max} — ${percent}%`;
    const label = settings.label && <div className="progress-widget__label">{settings.label}</div>;

    switch (variant) {
        case 'semi-ring': {
            const sweep = arcPath(50, 50, 40, -90, -90 + 180 * (percent / 100));
            return (
                <div className="progress-widget" title={tooltip}>
                    <svg viewBox="0 0 100 60" className="progress-widget__semi">
                        <path d={arcPath(50, 50, 40, -90, 90)} fill="none" stroke="#e5e7eb" strokeWidth="10" strokeLinecap="round" />
                        <path d={sweep} fill="none" stroke={accent} strokeWidth="10" strokeLinecap="round" />
                        <text x="50" y="48" textAnchor="middle" className="progress-widget__semi-text">{percent}%</text>
                    </svg>
                    {label}
                </div>
            );
        }

        case 'linear-bar':
            return (
                <div className="progress-widget" title={tooltip}>
                    <div className="bar-meter bar-meter--horizontal">
                        <div className="bar-meter__track">
                            <div className="bar-meter__fill" style={{ width: `${percent}%`, backgroundColor: accent }} />
                        </div>
                        <div className="bar-meter__value">{percent}%</div>
                    </div>
                    {label}
                </div>
            );

        case 'vertical-bar':
            return (
                <div className="progress-widget" title={tooltip}>
                    <div className="bar-meter bar-meter--vertical">
                        <div className="bar-meter__track bar-meter__track--vertical">
                            <div className="bar-meter__fill bar-meter__fill--vertical" style={{ height: `${percent}%`, backgroundColor: accent }} />
                        </div>
                        <div className="bar-meter__value">{percent}%</div>
                    </div>
                    {label}
                </div>
            );

        case 'stepped': {
            const steps = 5;
            const litSteps = Math.round((percent / 100) * steps);
            return (
                <div className="progress-widget" title={tooltip}>
                    <div className="progress-widget__steps">
                        {Array.from({ length: steps }).map((_, i) => (
                            <div key={i} className="progress-widget__step" style={i < litSteps ? { backgroundColor: accent } : undefined} />
                        ))}
                    </div>
                    <div className="progress-widget__label">{percent}%{settings.label ? ` ${settings.label}` : ''}</div>
                </div>
            );
        }

        case 'dial': {
            const needleAngle = -120 + 240 * (percent / 100);
            const tip = polarToCartesian(50, 55, 32, needleAngle);
            return (
                <div className="progress-widget" title={tooltip}>
                    <svg viewBox="0 0 100 70" className="progress-widget__dial">
                        <path d={arcPath(50, 55, 40, -120, 120)} fill="none" stroke="#e5e7eb" strokeWidth="8" strokeLinecap="round" />
                        <path d={arcPath(50, 55, 40, -120, needleAngle)} fill="none" stroke={accent} strokeWidth="8" strokeLinecap="round" />
                        <line x1="50" y1="55" x2={tip.x} y2={tip.y} stroke="#374151" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="50" cy="55" r="3.5" fill="#374151" />
                    </svg>
                    {label}
                </div>
            );
        }

        case 'concentric': {
            const r1 = 40;
            const r2 = 28;
            const c1 = 2 * Math.PI * r1;
            const c2 = 2 * Math.PI * r2;
            return (
                <div className="progress-widget" title={tooltip}>
                    <svg viewBox="0 0 100 100" className="progress-widget__concentric">
                        <circle cx="50" cy="50" r={r1} fill="none" stroke="#e5e7eb" strokeWidth="6" />
                        <circle
                            cx="50" cy="50" r={r1} fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round"
                            strokeDasharray={c1} strokeDashoffset={c1 * (1 - percent / 100)} transform="rotate(-90 50 50)"
                        />
                        <circle cx="50" cy="50" r={r2} fill="none" stroke="#f3f4f6" strokeWidth="6" />
                        <circle
                            cx="50" cy="50" r={r2} fill="none" stroke={`${accent}99`} strokeWidth="6" strokeLinecap="round"
                            strokeDasharray={c2} strokeDashoffset={c2 * (1 - Math.min(100, percent * 1.15) / 100)} transform="rotate(-90 50 50)"
                        />
                        <text x="50" y="55" textAnchor="middle" className="progress-widget__concentric-text">{percent}%</text>
                    </svg>
                    {label}
                </div>
            );
        }

        case 'liquid-fill':
            return (
                <div className="progress-widget" title={tooltip}>
                    <div className="progress-widget__tank">
                        <div className="progress-widget__tank-fill" style={{ height: `${percent}%`, backgroundColor: accent }} />
                        <span className="progress-widget__tank-text">{percent}%</span>
                    </div>
                    {label}
                </div>
            );

        case 'badge-percent':
            return (
                <div className="progress-widget" title={tooltip}>
                    <div className="progress-widget__badge" style={{ borderColor: accent, color: textColor }}>
                        <i className="fas fa-circle-notch"></i>
                        <span>{percent}%</span>
                    </div>
                    {label}
                </div>
            );

        case 'checklist': {
            const segCount = 10;
            const litCount = Math.round((percent / 100) * segCount);
            return (
                <div className="progress-widget" title={tooltip}>
                    <div className="progress-widget__checklist">
                        {Array.from({ length: segCount }).map((_, i) => (
                            <i
                                key={i}
                                className={i < litCount ? 'fas fa-square-check' : 'far fa-square'}
                                style={i < litCount ? { color: accent } : undefined}
                            ></i>
                        ))}
                    </div>
                    <div className="progress-widget__label">{percent}%{settings.label ? ` ${settings.label}` : ''}</div>
                </div>
            );
        }

        case 'ring':
        default:
            return (
                <div className="progress-widget" title={tooltip}>
                    <CircularProgressbar
                        value={percent}
                        text={`${percent}%`}
                        styles={buildStyles({
                            pathColor: accent,
                            textColor: settings.textColor || '#111827',
                            trailColor: '#e5e7eb',
                        })}
                    />
                    {label}
                </div>
            );
    }
};

export default ProgressWidget;
